// src/Components/blog.jsx
import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const categories = ["All", "Education", "Healthcare", "Empowerment", "Announcements"];

export default function BlogPage() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [blogPosts, setBlogPosts] = useState([]);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  const navigate = useNavigate();

  // Handle responsive layout
  useEffect(() => {
    function handleResize() {
      if (typeof window !== "undefined") {
        setIsMobile(window.innerWidth < 768);
      }
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Live Firestore listener
  useEffect(() => {
    const q = query(collection(db, "blogs"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const items = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setBlogPosts(items);
      },
      (err) => {
        console.error("Failed to load blogs:", err);
      }
    );
    return () => unsub();
  }, []);

  // Fast fallback for immediate UX (if created via admin)
  useEffect(() => {
    const onBlogCreated = (e) => {
      const newBlog = e.detail;
      if (!newBlog) return;

      setBlogPosts((prev) => {
        // avoid duplicate if snapshot already added it
        if (prev.some((b) => b.id === newBlog.id)) return prev;
        return [{ ...newBlog }, ...prev];
      });
    };

    window.addEventListener("blogCreated", onBlogCreated);
    return () => window.removeEventListener("blogCreated", onBlogCreated);
  }, []);

  const filteredPosts = blogPosts.filter((post) => {
    const title = (post.title || "").toLowerCase();
    const matchesSearch = title.includes(search.toLowerCase());
    const matchesCategory = filter === "All" || post.category === filter;
    return matchesSearch && matchesCategory;
  });

  return (
    <div
      className="blog-container"
      style={{ padding: 16, maxWidth: 1000, margin: "0 auto" }}
    >
      <h2>📢 Navveer Blog & Announcements</h2>

      {/* Controls */}
      <div
        className="blog-controls"
        style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}
      >
        <input
          type="text"
          placeholder="Search articles..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 180, padding: 8 }}
        />

        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ padding: 8 }}
        >
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {/* Blog cards */}
      <div
        className="blog-grid"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {filteredPosts.length ? (
          filteredPosts.map((post) => (
            <div
              className="blog-card"
              key={post.id}
              style={{
                border: "1px solid #ddd",
                borderRadius: 8,
                overflow: "hidden",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                backgroundColor: "#fff",
                minHeight: 160,
              }}
            >
              {/* LEFT: Blog image (or placeholder) */}
              {post.imageUrl ? (
                <div
                  style={{
                    width: isMobile ? "100%" : 220,
                    height: isMobile ? 180 : "100%",
                    flexShrink: 0,
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={post.imageUrl}
                    alt={post.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>
              ) : (
                <div
                  style={{
                    width: isMobile ? "100%" : 220,
                    height: isMobile ? 120 : "100%",
                    minHeight: isMobile ? 120 : 160,
                    backgroundColor: "#f3f3f3",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: 14,
                    color: "#888",
                    flexShrink: 0,
                  }}
                >
                  No Image
                </div>
              )}

              {/* RIGHT: Text content */}
              <div
                style={{
                  flex: 1,
                  padding: 14,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <h3 style={{ margin: "0 0 6px 0" }}>{post.title}</h3>

                  <p
                    className="meta"
                    style={{ margin: "0 0 8px 0", fontSize: 12, color: "#666" }}
                  >
                    {post.author || "Unknown"} • {post.date || ""} •{" "}
                    <span style={{ fontWeight: 600 }}>{post.category}</span>
                  </p>

                  <p
                    className="excerpt"
                    style={{
                      margin: "0 0 12px 0",
                      fontSize: 14,
                      color: "#333",
                    }}
                  >
                    {post.excerpt}
                  </p>
                </div>

                <button
                  className="read-more"
                  onClick={() => navigate(`/blogs/${post.id}`)}
                  style={{
                    alignSelf: "flex-start",
                    padding: "6px 12px",
                    borderRadius: 4,
                    border: "none",
                    backgroundColor: "#007bff",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: 14,
                    marginTop: 4,
                  }}
                >
                  Read More
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="no-results">No articles found for your search/filter.</p>
        )}
      </div>
    </div>
  );
}
